import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ReportsService } from '@/services/reports.service'
import type { Report, ReportCreatePayload, ReportUpdatePayload} from '@/entities/report'
import NotificationMessageService from '@/services/notificationmessage.service'

const service = new ReportsService()

export const useReportsStore = defineStore('reports', () => {
  // --- State ---
  const reports = ref<Report[]>([])
  const report = ref<Report | null>(null)
  const loadedValue = ref<number | null>(null)

  // --- Getters ---
  const getReports = computed(() => reports.value)
  const getReport = computed(() => report.value)
  const getLoadedValue = computed(() => loadedValue.value)

  // --- Actions ---
  async function fetchOrderReports(orderId: number) {
    try {
      reports.value = await service.getReports(orderId) ?? []
    } catch (err) {
      console.warn(`error while fetching reports`, err)
    }
  }

  async function fetchOrderReport(fetchData: {
    orderId: number
    orderFileId: number
    fileName: string
  }) {
    if (fetchData?.orderId > 0 && fetchData?.orderFileId > 0) {
      try {
        await service.getReport(fetchData.orderId, fetchData.orderFileId, fetchData.fileName)
      } catch (err: any) {
        err.status === 403
          ? NotificationMessageService.showWarning("Not Enough Permissions")
          : NotificationMessageService.showError('Error while loading report')
      }
    }
  }

  async function deleteReport(orderId: number, orderFileId: number): Promise<boolean> {
    if (orderId > 0 && orderFileId > 0) {
      try {
        const err = await service.deleteReport(orderId, orderFileId)
        return !err
      } catch (err) {
        console.warn(`error while deleting file id ${orderFileId} of order ${orderId}`)
      }
    }
    return false
  }


  async function insertOrderReport(orderId: number, payload: ReportCreatePayload): Promise<Report | null> {
    if (orderId && payload) {
      try {
        return await service.insertReport(orderId, payload)
      } catch (err) {
        console.warn(`Error while inserting report of order ${orderId}`)
      }
    }
    return null
  }

  async function updateOrderReport(orderId: number, payload: ReportUpdatePayload): Promise<boolean> {
    try {
      const err =  await service.updateReport(orderId, payload.orderFileId, payload)
      return !err
    } catch (err) {
      console.warn(`Error while updating report of order ${orderId}`, err)
      throw err
    }
  }

  function fetchLoadedValue() {
    loadedValue.value = service.getLoadedValue()
  }

  return {
    // State
    reports,
    report,
    loadedValue,

    // Getters
    getReports,
    getReport,
    getLoadedValue,

    // Actions
    fetchOrderReports,
    fetchOrderReport,
    deleteReport,
    insertOrderReport,
    updateOrderReport,
    fetchLoadedValue,
  }
})
